import { memo, MutableRefObject, useEffect, useRef, useState } from "react";
import { InputText } from 'component-library/index';
import React from "react";
import { useField } from "../../../providers/ModernAcquisitionProvider/useField";
import { useAcquisitionStore } from "../../../providers/ModernAcquisitionProvider/useAcquisitionStore";
import { DynamicContainer, Label } from "../../../importCL";
import { ACTIONS, TErrorType, TFieldState, TInputField } from "../../../providers/ModernAcquisitionProvider/types";
import { dateHandleBlur, dateHandleChange } from "./components/InputDate/util/InputDateValidator";
import { addUnderscores } from "../../../component-library/utils/utils";
import './DateOfBirthField.css';
import { isValidAge } from "./components/InputDate/util/ageVerification";
import { useAllFieldSubscription } from "../../../providers/ModernAcquisitionProvider/useSubscription";
import isDayValueValid from "./components/InputDate/util/isDayValueValid";
import getLastDayOfTheMonth from "./components/InputDate/util/getLastDayOfTheMonth";


export interface IDateOfBirthFieldWithProviderProps {
    label: string;
    id?: string;
    helperText?: string;
    required?: boolean;
    disabled?: boolean;
    ctaId?: string;
    isAuthorizedUser: boolean;
    errorMessage?: string;
    validateWithEnter?: boolean;
    onBlurCallback?: () => void;
    onFocusCallback?: () => void;
    onKeyPressCallback?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
}

export interface IDateInputProps {
    fieldName: 'dateOfBirthMonth' | 'dateOfBirthDay' | 'dateOfBirthYear' | 'authorizedUserDateOfBirthDay' | 'authorizedUserDateOfBirthMonth' | 'authorizedUserDateOfBirthYear';
    type: 'month' | 'day' | 'year';
    onBlurCallback?: () => void;
    onFocusCallback?: () => void;
    onKeyPressCallback?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    dobState: TDobState;
    inputRef: MutableRefObject<HTMLInputElement>;
    isAuthorizedUser: boolean;
    validateWithEnter?: boolean;
    label?: string;
    required?: boolean;
    disabled?: boolean;
    setAdditionalText?: (additionalText: string) => void;
    additionalText?: string;
};

export type TDateState = {
    month: TInputField;
    day: TInputField;
    year: TInputField;
}

export type TDobState = {
    getCurrentDateState: () => TDateState;
    maxAge: number;
    minAge: number;
    inputRefs: {
        month: MutableRefObject<HTMLInputElement>;
        day: MutableRefObject<HTMLInputElement>;
        year: MutableRefObject<HTMLInputElement>;
    }
    activeInput: string;
}

const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const MemoizedInputText = memo(InputText);

const DateInput = (props: IDateInputProps & React.HTMLAttributes<HTMLInputElement>) => {
    const { field, updateField } = useField(props.fieldName);
    const inputIdentifier = props.id || props.label;

    const getAdditionalText = (value: string): string => {
        if (props.type === 'month') {
            return monthNames[Number(value) - 1];
        }
        return value;
    }

    const autotabHandler = (type: string, dobState: TDobState, value: string): void => {
        if (type === 'month' && value.length == 2) {
            dobState.inputRefs.day.current.focus();
        } else if (type === 'day' && value.length == 2) {
            dobState.inputRefs.year.current.focus();
        }
    }

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const cleanedValue = event.target.value.replace(/[^0-9]+/g, '')

        //Set up initial changes
        if (field.value !== event.target.value) {
            updateField({ ...field, state: 'neutral', value: cleanedValue });
        } else {
            updateField({ ...field, value: cleanedValue });
        }

        //Run progressive validation handler for date type
        const inputFieldState = dateHandleChange(props.dobState.getCurrentDateState(), props.type, props.dobState);

        //update the field with new state
        updateField({ ...inputFieldState });

        if (inputFieldState.errorType == 'None') {
            autotabHandler(props.type, props.dobState, inputFieldState.value);
        }
    }

    //This runs when autotabbed and on blur
    const handleBlur = (): void => {

        //Run deeper blur validations
        const inputFieldState = dateHandleBlur(props.dobState.getCurrentDateState(), props.type);

        //Update field with new state
        updateField({ ...inputFieldState });

        props.setAdditionalText(getAdditionalText(inputFieldState.value));
        props.onBlurCallback && props.onBlurCallback();
    }

    const handleFocus = (): void => {
        props.setAdditionalText('');
        updateField({ ...field, state: 'neutral' })
        props.dobState.activeInput = props.type;
        props.onFocusCallback && props.onFocusCallback();
    }

    const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        props.onKeyPressCallback && props.onKeyPressCallback(event);
    }

    return (
        <div className='flex flex-col'>
            <MemoizedInputText
                id={`${props.id}__${props.isAuthorizedUser ? 'au' : ''}${props.type}`}
                className='DateInput !max-w-[5.8125rem] md:!max-w-none !min-w-[3.25rem]'
                maxLength={props.type === 'year' ? 4 : 2}
                useSuccessIcon={props.type === 'year'}
                state={field.state}
                value={field.value}
                onChange={handleChange}
                onBlur={handleBlur}
                onKeyPress={handleKeyPress}
                onFocus={handleFocus}
                inputMode='numeric'
                ref={props.inputRef}
                aria-describedby={`${inputIdentifier}__input_date__helper ${inputIdentifier}__input_date__desc`}
                aria-errormessage={`${inputIdentifier}__input_date__adv__helper`}
                aria-required={props.required}
                aria-disabled={props.disabled}
                aria-invalid={field.state === 'error'}
                aria-label={`${props.type.charAt(0).toUpperCase() + props.type.slice(1)} Field`}
            />
            {field.state !== 'error' && <span className="pb-2 pl-4 pt-2 text-grey-mine-shaft">
                {props.additionalText}
            </span>}
        </div>
    )
}

export const DateOfBirthFieldWithProvider = memo(function DateOfBirthFieldWithProvider(props: IDateOfBirthFieldWithProviderProps & React.HTMLAttributes<HTMLInputElement>, ...rest) {

    const { getStateCopy, dispatch } = useAcquisitionStore();
    const [isFieldError, setIsFieldError] = useState(false);
    const [hideIcon, setHideIcon] = useState(false);
    const monthInputRef = useRef<HTMLInputElement>(null);
    const dayInputRef = useRef<HTMLInputElement>(null);
    const yearInputRef = useRef<HTMLInputElement>(null);
    const { fields } = useAllFieldSubscription();
    const [monthText, setMonthText] = useState('');
    const [dayText, setDayText] = useState('');
    const [yearText, setYearText] = useState('');

    const dobStateRef = useRef<TDobState>({
        getCurrentDateState: buildCurrentDateState,
        maxAge: 101,
        minAge: props.isAuthorizedUser ? 15 : 18,
        inputRefs: {
            month: monthInputRef,
            day: dayInputRef,
            year: yearInputRef,
        },
        activeInput: undefined,
    });

    const bulkDispatch = (keepValues: boolean, errorType: TErrorType, state: TFieldState) => {
        let currentDateState = buildCurrentDateState();
        if (props.isAuthorizedUser) {
            dispatch({
                type: ACTIONS.BULK_UPDATE_FIELD,
                payload: {
                    authorizedUserDateOfBirthMonth: {
                        value: currentDateState.month.value,
                        errorType: errorType,
                        state: state
                    },
                    authorizedUserDateOfBirthDay: {
                        value: currentDateState.day.value,
                        errorType: errorType,
                        state: state
                    },
                    authorizedUserDateOfBirthYear: {
                        value: currentDateState.year.value,
                        errorType: errorType,
                        state: state
                    }
                }
            });
        } else {
            dispatch({
                type: ACTIONS.BULK_UPDATE_FIELD,
                payload: {
                    dateOfBirthMonth: {
                        value: currentDateState.month.value,
                        errorType: errorType,
                        state: state
                    },
                    dateOfBirthDay: {
                        value: currentDateState.day.value,
                        errorType: errorType,
                        state: state
                    },
                    dateOfBirthYear: {
                        value: currentDateState.year.value,
                        errorType: errorType,
                        state: state
                    }
                }
            });
        }
    }

    function buildCurrentDateState(): TDateState {
        const acquistionDataCopy = getStateCopy();
        if (props.isAuthorizedUser) {
            return {
                month: {
                    value: acquistionDataCopy.authorizedUserDateOfBirthMonth.value,
                    state: acquistionDataCopy.authorizedUserDateOfBirthMonth.state,
                    errorType: acquistionDataCopy.authorizedUserDateOfBirthMonth.errorType,
                },
                day: {
                    value: acquistionDataCopy.authorizedUserDateOfBirthDay.value,
                    state: acquistionDataCopy.authorizedUserDateOfBirthDay.state,
                    errorType: acquistionDataCopy.authorizedUserDateOfBirthDay.errorType,
                },
                year: {
                    value: acquistionDataCopy.authorizedUserDateOfBirthYear.value,
                    state: acquistionDataCopy.authorizedUserDateOfBirthYear.state,
                    errorType: acquistionDataCopy.authorizedUserDateOfBirthYear.errorType,
                }
            }
        } else {
            return {
                month: {
                    value: acquistionDataCopy.dateOfBirthMonth.value,
                    state: acquistionDataCopy.dateOfBirthMonth.state,
                    errorType: acquistionDataCopy.dateOfBirthMonth.errorType,
                },
                day: {
                    value: acquistionDataCopy.dateOfBirthDay.value,
                    state: acquistionDataCopy.dateOfBirthDay.state,
                    errorType: acquistionDataCopy.dateOfBirthDay.errorType,
                },
                year: {
                    value: acquistionDataCopy.dateOfBirthYear.value,
                    state: acquistionDataCopy.dateOfBirthYear.state,
                    errorType: acquistionDataCopy.dateOfBirthYear.errorType,
                }
            }
        }

    }

    const onBlurCallback = () => {

        validateFields();

        props.onBlurCallback && props.onBlurCallback();
    }

    const validateFields = () => {
        const currentDateState = buildCurrentDateState();

        if (currentDateState[dobStateRef.current.activeInput].errorType !== 'None') {
            bulkDispatch(true, 'invalidInput', 'error');
            setIsFieldError(true);
        } else if (currentDateState.month.value && currentDateState.day.value && currentDateState.year.value) {
            const lastDayOfMonth = getLastDayOfTheMonth({ month: currentDateState.month.value, year: currentDateState.year.value });
            if (currentDateState.month.errorType == 'None' && currentDateState.day.errorType == 'None' && currentDateState.year.errorType == 'None' || isValidAge(currentDateState, dobStateRef.current.maxAge, dobStateRef.current.minAge)) {
                if (isDayValueValid(currentDateState.day.value, lastDayOfMonth) && isValidAge(currentDateState, dobStateRef.current.maxAge, dobStateRef.current.minAge)) {
                    bulkDispatch(true, 'None', 'success');
                    setIsFieldError(false);
                    setHideIcon(false);
                    setMonthText(monthNames[Number(currentDateState.month.value) - 1]);
                    setDayText(currentDateState.day.value);
                    setYearText(currentDateState.year.value);
                } else {
                    bulkDispatch(true, 'invalidInput', 'error');
                    setIsFieldError(true);
                }
            } else {
                bulkDispatch(true, 'invalidInput', 'error');
                setIsFieldError(true);
            }
        }
    }
    //Initialize all fields to required
    useEffect(() => {
        const currentDateState = buildCurrentDateState();
        if (props.isAuthorizedUser) {
            dispatch({
                type: ACTIONS.BULK_UPDATE_FIELD,
                payload: {
                    authorizedUserDateOfBirthMonth: {
                        value: currentDateState.month.value,
                        errorType: currentDateState.month.value ? currentDateState.month.errorType : 'requiredField',
                        state: 'neutral'
                    },
                    authorizedUserDateOfBirthDay: {
                        value: currentDateState.day.value,
                        errorType: currentDateState.day.value ? currentDateState.day.errorType : 'requiredField',
                        state: 'neutral'
                    },
                    authorizedUserDateOfBirthYear: {
                        value: currentDateState.year.value,
                        errorType: currentDateState.year.value ? currentDateState.year.errorType : 'requiredField',
                        state: 'neutral'
                    },
                }
            });
        }
        else {
            dispatch({
                type: ACTIONS.BULK_UPDATE_FIELD,
                payload: {
                    dateOfBirthMonth: {
                        value: currentDateState.month.value,
                        errorType: currentDateState.month.value ? currentDateState.month.errorType : 'requiredField',
                        state: 'neutral'
                    },
                    dateOfBirthDay: {
                        value: currentDateState.day.value,
                        errorType: currentDateState.day.value ? currentDateState.day.errorType : 'requiredField',
                        state: 'neutral'
                    },
                    dateOfBirthYear: {
                        value: currentDateState.year.value,
                        errorType: currentDateState.year.value ? currentDateState.year.errorType : 'requiredField',
                        state: 'neutral'
                    },
                }
            });
        }
    }, []);

    //Needs to listen to error state to adjust labels since they are independent
    useEffect(() => {
        const currentDateState = buildCurrentDateState();
        if ((currentDateState.day.state === 'error' || currentDateState.month.state === 'error' || currentDateState.year.state === 'error')) {
            bulkDispatch(true, 'invalidInput', 'error');
            setIsFieldError(true);
        } else {
            setIsFieldError(false);
        }
    }, [fields.authorizedUserDateOfBirthDay.state, fields.authorizedUserDateOfBirthMonth.state, fields.authorizedUserDateOfBirthYear.state, fields.dateOfBirthDay.state, fields.dateOfBirthMonth.state, fields.dateOfBirthYear.state])

    return (
        <DynamicContainer tag='div' displayTag className={`DateOfBirthField ${hideIcon ? 'hideIcon' : ''}`} id={props.isAuthorizedUser ? 'auDob' : 'dob'} {...rest}>
            <Label
                helperText={props.helperText}
                required
                error={isFieldError}
                alternate
            >
                {props.label}
            </Label>
            <div className='flex gap-2'>
                <DateInput
                    {...props}
                    type='month'
                    dobState={dobStateRef.current}
                    inputRef={monthInputRef}
                    onKeyPressCallback={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter' && props.validateWithEnter) {
                            validateFields();
                        }
                    }}
                    onFocusCallback={() => {
                        setHideIcon(true);
                    }}
                    onBlurCallback={onBlurCallback}
                    fieldName={props.isAuthorizedUser ? 'authorizedUserDateOfBirthMonth' : 'dateOfBirthMonth'}
                    setAdditionalText={setMonthText}
                    additionalText={monthText}
                />
                <p className='InputDate__separator'>-</p>
                <DateInput
                    {...props}
                    type='day'
                    dobState={dobStateRef.current}
                    inputRef={dayInputRef}
                    onKeyPressCallback={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter' && props.validateWithEnter) {
                            validateFields();
                        }
                    }}
                    onFocusCallback={() => {
                        setHideIcon(true);
                    }}
                    onBlurCallback={onBlurCallback}
                    fieldName={props.isAuthorizedUser ? 'authorizedUserDateOfBirthDay' : 'dateOfBirthDay'}
                    setAdditionalText={setDayText}
                    additionalText={dayText}
                />
                <p className='InputDate__separator'>-</p>
                <DateInput
                    {...props}
                    type='year'
                    dobState={dobStateRef.current}
                    inputRef={yearInputRef}
                    onKeyPressCallback={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === 'Enter' && props.validateWithEnter) {
                            validateFields();
                        }
                    }}
                    onFocusCallback={() => {
                        setHideIcon(true);
                    }}
                    onBlurCallback={onBlurCallback}
                    fieldName={props.isAuthorizedUser ? 'authorizedUserDateOfBirthYear' : 'dateOfBirthYear'}
                    setAdditionalText={setYearText}
                    additionalText={yearText}
                />
            </div>
            <p
                className={isFieldError && `DateOfBirthField__errorText text-xs min-h-5 font-bold text-feedback-error`}
                id={`${addUnderscores(props.id, props.label?.toString())}__input_date__adv__helper`}
                aria-live='assertive'
            >
                {isFieldError && props.errorMessage}
            </p>
        </DynamicContainer>
    );
});

